import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { SECTION_IDS, site } from '../content/site';
import { gsap, ScrollTrigger, useGSAP, EASE } from '../lib/gsap';
import { useLenis } from '../lib/SmoothScroll';
import { useDemoMode } from '../components/manifesto/useDemoMode';
import { AgentDemo } from '../components/manifesto/AgentDemo';
import { DayTimeline } from '../components/manifesto/DayTimeline';
import { TerritoryView } from '../components/manifesto/TerritoryView';
import { ScaleSteps } from '../components/manifesto/ScaleSteps';
import { ClosingLine } from '../components/manifesto/ClosingLine';
import {
	REST,
	SCENE,
	SWITCH,
	agentRun,
	boxIn,
	dayShift,
	repaintOf,
	revealTitle,
	scrubWords,
	territoryFlow,
} from '../components/manifesto/motion';
import { resetThreads } from '../components/manifesto/threads';
import { glue } from '../components/manifesto/typeset';

const TITLE_ID = 'manifeste-titre';

/**
 * The stage: a frosted Mineral window over the dimmed fiber weave (tinted shadow, hairline,
 * inner light). Phones get a more opaque surface instead of a backdrop blur.
 * Dark theme: a Forest Night window, a Mineral hairline, no inner light.
 */
const FRAME =
	'rounded-card border border-graphite/[0.08] bg-[rgba(247,247,240,0.88)] shadow-[0_40px_90px_-50px_rgba(41,77,59,0.5),inset_0_1px_0_rgba(251,251,247,0.9)] md:bg-[rgba(247,247,240,0.74)] md:backdrop-blur-md dark:border-mineral/10 dark:bg-forest-night/90 dark:shadow-[0_40px_90px_-50px_rgba(15,29,22,0.9)] dark:md:bg-forest-night/75';

/**
 * Stage cell of the pinned grid: the right 7 columns across all rows. The frame and the
 * three layers share it exactly, so the layers read as one window whose content changes.
 */
const STAGE =
	'col-span-7 col-start-6 row-span-3 row-start-1 aspect-[0.86] max-h-full w-full self-center rounded-card xl:aspect-[1.2]';

const FLOW_BEAT = 'lg:grid lg:grid-cols-12 lg:items-center lg:gap-x-10';

/** Paint order of the stage layers: the task above the day while it lands in it, the territory on top. */
const LAYER_ORDER = ['z-[2]', 'z-[1]', 'z-[3]'];

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

type Point = { x: number; y: number };
type Geometry = { card: Point; block: Point; shrink: number; center: Point; city: Point; zoomOut: number };

/**
 * "De la tâche à toute l'entreprise": the demonstration that replaced the text manifesto.
 * Three scales of the same idea, each shown by a real visual: an agent handling one
 * request under human control (task), a day whose chores flow into automations and
 * whose freed time turns gold (team), the data of a whole territory flowing into
 * agents whose signals reach the people who decide (territory).
 * Then the closing sentence, revealed word by word.
 *
 * Desktop (see useDemoMode): one scene pinned for SCENE.pin viewports. Left, the title,
 * the current beat and the scale steps; right, one stage where a camera zooms out
 * from the task into its block of the day, then from the day into the agents node of
 * the territory, whose flow then plays. Scrubbed and reversible; refresh-safe: every tween is a fromTo, and what
 * callbacks drive (left column, day threads) is resynced on each refresh.
 *
 * Accessibility: the three beats always stay in the DOM and in the accessibility tree,
 * in reading order (text, then its visual). Pinned, the inactive ones are only made
 * transparent (and take no pointer events): a screen reader reads all three in order,
 * nothing is announced twice, and when keyboard focus enters a beat that is not on
 * stage (the approval button), the scene scrolls to that beat.
 * The scale steps are buttons with aria-current="step".
 *
 * Stacked (tablet, phone, short screens): each text is followed by its own framed
 * visual; the agent run and the day shift play once on enter. Reduced motion: the
 * markup is the final state (steps done, approval pending and clickable, day after,
 * territory fully drawn and validated).
 */
export function Manifesto() {
	const { title, beats, closing, demo, day, territory } = site.manifesto;
	const mode = useDemoMode();
	const pinned = mode === 'pinned';
	const lenis = useLenis();
	const root = useRef<HTMLElement>(null);
	const scene = useRef<HTMLDivElement>(null);
	const pin = useRef<ScrollTrigger | null>(null);
	const activeRef = useRef(0);
	const [active, setActive] = useState(0);
	/** Pinned: current timeline progress, and where each beat is fully composed (set per build). */
	const progressRef = useRef(0);
	const readyRef = useRef([0, 0, 0]);

	const scrollToBeat = useCallback(
		(index: number) => {
			const trigger = pin.current;
			if (!trigger) return;
			const y = trigger.start + (trigger.end - trigger.start) * (REST[index] ?? 0);
			if (lenis) lenis.scrollTo(y, { duration: 1.3 });
			else window.scrollTo({ top: y, behavior: 'smooth' });
		},
		[lenis],
	);

	// Keyboard focus entering a beat that is not composed on stage (another beat, or the
	// approval not revealed yet) brings that beat on stage, so focus is never invisible.
	const onBeatFocus = (index: number) => {
		if (!pinned) return;
		const progress = progressRef.current;
		const until = index < SWITCH.length ? SWITCH[index] : 1;
		if (progress < readyRef.current[index] || progress > until) scrollToBeat(index);
	};

	useGSAP(
		(context, contextSafe) => {
			const section = root.current;
			if (!section || mode === 'static') return undefined;
			const q = gsap.utils.selector(section);
			const cleanups: Array<() => void> = [];

			const heading = q('[data-demo-title]')[0];
			if (heading instanceof HTMLElement) {
				cleanups.push(revealTitle(heading, context, section, pinned ? 'top 72%' : 'top 82%'));
			}
			const closingLine = q('[data-closing]')[0];
			if (closingLine) scrubWords(closingLine);

			const layers = q('[data-beat-layer]') as HTMLElement[];
			const texts = q('[data-beat-text]') as HTMLElement[];
			const [agentLayer, dayLayer, territoryLayer] = layers;
			const card = q('[data-agent-card]')[0];

			if (mode === 'flow') {
				q('[data-beat-inner]').forEach((inner) => {
					gsap.fromTo(
						inner,
						{ y: 28, opacity: 0 },
						{ y: 0, opacity: 1, duration: 1.05, ease: EASE, scrollTrigger: { trigger: inner, start: 'top 86%', once: true } },
					);
				});
				layers.forEach((layer) => {
					gsap.fromTo(
						layer,
						{ y: 40, opacity: 0 },
						{ y: 0, opacity: 1, duration: 1.15, ease: EASE, scrollTrigger: { trigger: layer, start: 'top 90%', once: true } },
					);
				});
				// The agent works once, when its card is well in view; the button is live at the end.
				if (card) {
					const run = agentRun(card).pause(0);
					ScrollTrigger.create({ trigger: card, start: 'top 58%', once: true, onEnter: () => run.play() });
					// Keyboard focus reaching the (still hidden) approval finishes the run at once.
					const finish = () => {
						if (run.progress() < 1) run.timeScale(6).play();
					};
					card.addEventListener('focusin', finish);
					cleanups.push(() => card.removeEventListener('focusin', finish));
				}
				// The day shifts once, when the whole calendar is readable.
				if (dayLayer) {
					const shift = dayShift(dayLayer).pause(0);
					const calendar = dayLayer.querySelector('[data-day-flow]') ?? dayLayer;
					ScrollTrigger.create({ trigger: calendar, start: 'center 62%', once: true, onEnter: () => shift.play() });
				}
				// The territory flows once, when the diagram is well in view.
				if (territoryLayer) {
					const flow = territoryFlow(territoryLayer).pause(0);
					const diagram = territoryLayer.querySelector('[data-territory-field]') ?? territoryLayer;
					ScrollTrigger.create({ trigger: diagram, start: 'top 62%', once: true, onEnter: () => flow.play() });
				}
				return () => {
					cleanups.forEach((cleanup) => cleanup());
					resetThreads(section);
				};
			}

			// ---- Pinned scene ---------------------------------------------------------
			const sceneEl = scene.current;
			if (!sceneEl || !agentLayer || !dayLayer || !territoryLayer) return undefined;
			const agentCam = q('[data-cam="agent"]')[0];
			const dayOut = q('[data-cam="day-out"]')[0];
			const dayIn = q('[data-cam="day-in"]')[0];
			const territoryCam = q('[data-cam="territory"]')[0];
			const block = q('[data-day-target]')[0];
			const ring = q('[data-day-focus]')[0];
			const focus = q('[data-territory-core]')[0];
			const frame = q('[data-stage-frame]')[0];
			const steps = q('[data-steps]')[0];
			const fill = q('[data-steps-fill]')[0];
			const head = q('[data-steps-head]')[0];

			// Camera geometry, from layout boxes (cached until the next refresh).
			let geometry: Geometry | null = null;
			const measure = (): Geometry => {
				const c = boxIn(card, agentLayer);
				const d = boxIn(block, dayLayer);
				const f = boxIn(focus, territoryLayer);
				const width = dayLayer.offsetWidth;
				return {
					card: { x: c.x + c.w / 2, y: c.y + c.h / 2 },
					block: { x: d.x + d.w / 2, y: d.y + d.h / 2 },
					shrink: clamp(c.h / Math.max(1, d.h), 1.5, 7),
					center: { x: width / 2, y: dayLayer.offsetHeight / 2 },
					city: { x: f.x + f.w / 2, y: f.y + f.h / 2 },
					// The day lands at the size of the agents node.
					zoomOut: clamp(width / Math.max(1, f.w), 1.5, 3.4),
				};
			};
			const g = (): Geometry => {
				if (!geometry) geometry = measure();
				return geometry;
			};
			const forget = () => {
				geometry = null;
			};
			ScrollTrigger.addEventListener('refreshInit', forget);
			cleanups.push(() => ScrollTrigger.removeEventListener('refreshInit', forget));

			// Left column: one beat on stage at a time (opacity only, all stay readable).
			let current = activeRef.current;
			texts.forEach((text, index) => gsap.set(text, { opacity: index === current ? 1 : 0 }));
			const show = (contextSafe ?? ((fn: (index: number, direction: number) => void) => fn))(
				(index: number, direction: number) => {
					texts.forEach((text, i) => {
						if (i === index) {
							gsap.fromTo(
								text,
								{ opacity: 0, y: 18 * direction },
								{ opacity: 1, y: 0, duration: 0.85, delay: 0.12, ease: EASE, overwrite: true },
							);
						} else {
							gsap.to(text, { opacity: 0, y: -12 * direction, duration: 0.35, ease: 'power2.in', overwrite: true });
						}
					});
				},
			);
			const track = (progress: number) => {
				const index = progress < SWITCH[0] ? 0 : progress < SWITCH[1] ? 1 : 2;
				if (index === current) return;
				const direction = index > current ? 1 : -1;
				current = index;
				activeRef.current = index;
				show(index, direction);
				setActive(index);
			};

			// The pin: the scene holds for SCENE.pin viewports. Refreshed before every other
			// trigger of the page (Work, choreography), whatever the creation order.
			const hold = ScrollTrigger.create({
				trigger: sceneEl,
				start: 'top top',
				end: `+=${SCENE.pin * 100}%`,
				pin: true,
				anticipatePin: 1,
				refreshPriority: 1,
			});
			// The timeline starts while the scene slides in and ends with the pin. On load and
			// on every refresh ScrollTrigger positions it with callbacks suppressed: onRefresh
			// then resyncs what callbacks drive (the left column, the day threads).
			let repaintThreads: (() => void) | undefined;
			let timeline: gsap.core.Timeline | null = null;
			const sync = () => {
				if (!timeline) return;
				progressRef.current = timeline.progress();
				track(progressRef.current);
			};
			const master = gsap.timeline({
				defaults: { ease: 'none' },
				scrollTrigger: {
					trigger: sceneEl,
					start: `top ${Math.round(SCENE.lead * 100)}%`,
					end: () => hold.end,
					scrub: 0.6,
					invalidateOnRefresh: true,
					onRefresh: () => {
						repaintThreads?.();
						sync();
					},
				},
				onUpdate: sync,
			});
			timeline = master;
			pin.current = master.scrollTrigger ?? null;

			// 1. The agent run, scrubbed.
			const run = card ? agentRun(card) : null;
			if (run) master.add(run, SCENE.agentAt);

			// 2. Camera: the task shrinks into its block of the day (it stays on top while it
			//    lands, then dissolves), the day settles in around it.
			const morph = SCENE.morphAt;
			const morphLength = SCENE.morphLength;
			master
				.fromTo(
					agentCam,
					{ x: 0, y: 0, scale: 1, transformOrigin: () => `${g().card.x}px ${g().card.y}px` },
					{
						x: () => g().block.x - g().card.x,
						y: () => g().block.y - g().card.y,
						scale: () => 1 / g().shrink,
						duration: morphLength,
						ease: 'power2.inOut',
					},
					morph,
				)
				.fromTo(agentCam, { opacity: 1 }, { opacity: 0, duration: morphLength * 0.3, ease: 'power1.in' }, morph + morphLength * 0.7)
				.fromTo(
					dayIn,
					{ scale: 1.08, transformOrigin: () => `${g().block.x}px ${g().block.y}px` },
					{ scale: 1, duration: morphLength, ease: 'power2.out' },
					morph,
				)
				.fromTo(dayIn, { opacity: 0 }, { opacity: 1, duration: morphLength * 0.5, ease: 'power1.out' }, morph + morphLength * 0.2);
			if (ring) {
				master
					.fromTo(ring, { opacity: 0 }, { opacity: 1, duration: 0.3 }, morph + morphLength * 0.72)
					.fromTo(ring, { opacity: 1 }, { opacity: 0, duration: 0.4, immediateRender: false }, SCENE.dayAt + 0.2);
			}

			// 3. The day shifts, scrubbed.
			const shift = dayShift(dayLayer);
			master.add(shift, SCENE.dayAt);

			// 4. Camera: the day shrinks into the agents node while the territory zooms out from it.
			const zoom = SCENE.zoomAt;
			const zoomLength = SCENE.zoomLength;
			master
				.fromTo(
					dayOut,
					{ x: 0, y: 0, scale: 1, transformOrigin: () => `${g().center.x}px ${g().center.y}px` },
					{
						x: () => g().city.x - g().center.x,
						y: () => g().city.y - g().center.y,
						scale: () => 1 / g().zoomOut,
						duration: zoomLength,
						ease: 'power2.inOut',
					},
					zoom,
				)
				.fromTo(dayOut, { opacity: 1 }, { opacity: 0, duration: zoomLength * 0.34, ease: 'power1.in' }, zoom + zoomLength * 0.05)
				.fromTo(
					territoryCam,
					{
						x: () => g().center.x - g().city.x,
						y: () => g().center.y - g().city.y,
						scale: () => g().zoomOut,
						transformOrigin: () => `${g().city.x}px ${g().city.y}px`,
					},
					{ x: 0, y: 0, scale: 1, duration: zoomLength, ease: 'power2.inOut' },
					zoom,
				)
				.fromTo(
					territoryCam,
					{ opacity: 0 },
					{ opacity: 1, duration: zoomLength * 0.42, ease: 'power1.out' },
					zoom + zoomLength * 0.28,
				);

			// 5. The territory flows, scrubbed, while the camera settles on the agents node.
			const flow = territoryFlow(territoryLayer);
			master.add(flow, SCENE.territoryAt);
			const repaints = [repaintOf(shift), repaintOf(flow)];
			repaintThreads = () => repaints.forEach((repaint) => repaint?.());
			readyRef.current = [
				(SCENE.agentAt + (run ? run.duration() : 0)) / SCENE.length,
				(SCENE.dayAt + shift.duration()) / SCENE.length,
				(SCENE.territoryAt + flow.duration()) / SCENE.length,
			];

			// The scale steps thread: each third fills during its own beat.
			const [first, second] = SWITCH.map((progress) => progress * SCENE.length);
			const thirds: Array<[number, number, number]> = [
				[0, first, 0],
				[first, second, 1],
				[second, SCENE.length, 2],
			];
			thirds.forEach(([from, to, index]) => {
				const later = index > 0;
				if (fill) {
					master.fromTo(
						fill,
						{ scaleX: index / 3 },
						{ scaleX: (index + 1) / 3, duration: to - from, immediateRender: !later },
						from,
					);
				}
				if (head) {
					master.fromTo(
						head,
						{ xPercent: (index / 3) * 100 },
						{ xPercent: ((index + 1) / 3) * 100, duration: to - from, immediateRender: !later },
						from,
					);
				}
			});
			master.set({}, {}, SCENE.length);

			// Entrance, before the pin: the window rises, the first beat and the steps follow.
			const enter = () => ({ trigger: section, start: 'top 72%', once: true });
			const rising = [frame, agentLayer].filter(Boolean);
			gsap.fromTo(rising, { y: 64, opacity: 0 }, { y: 0, opacity: 1, duration: 1.3, ease: EASE, scrollTrigger: enter() });
			const firstInner = texts[0]?.querySelector('[data-beat-inner]');
			if (firstInner) {
				gsap.fromTo(
					firstInner,
					{ y: 26, opacity: 0 },
					{ y: 0, opacity: 1, duration: 1.1, delay: 0.15, ease: EASE, scrollTrigger: enter() },
				);
			}
			if (steps) {
				gsap.fromTo(steps, { opacity: 0 }, { opacity: 1, duration: 1, delay: 0.3, ease: EASE, scrollTrigger: enter() });
			}

			return () => {
				cleanups.forEach((cleanup) => cleanup());
				resetThreads(section);
				pin.current = null;
			};
		},
		{ scope: root, dependencies: [mode], revertOnUpdate: true },
	);

	const visual = (index: number): ReactNode => {
		const cam = pinned ? 'absolute inset-0' : 'relative';
		if (index === 0) {
			return (
				<div data-cam="agent" className={cam}>
					<div className={pinned ? 'flex h-full items-center justify-center p-5 xl:p-8' : 'p-5 sm:p-7'}>
						<AgentDemo demo={demo} className="mx-auto max-w-[34rem] xl:max-w-[44rem]" />
					</div>
				</div>
			);
		}
		if (index === 1) {
			return (
				<div data-cam="day-out" className={cam}>
					<div data-cam="day-in" className={cam}>
						<div className={pinned ? 'h-full p-5 xl:p-7' : 'p-5 sm:p-7'}>
							<DayTimeline day={day} fill={pinned} />
						</div>
					</div>
				</div>
			);
		}
		return (
			<div data-cam="territory" className={cam}>
				<div className={pinned ? 'h-full p-5 xl:p-7' : 'p-5 sm:p-7'}>
					<TerritoryView territory={territory} fill={pinned} />
				</div>
			</div>
		);
	};

	return (
		<section ref={root} id={SECTION_IDS.manifesto} aria-labelledby={TITLE_ID} className="relative">
			<div ref={scene} className={pinned ? 'relative h-[100dvh]' : 'relative pt-[clamp(6rem,14vw,12rem)]'}>
				<div
					className={
						pinned
							? 'container-site grid h-full grid-cols-12 grid-rows-[auto_minmax(0,1fr)_auto] gap-x-10 pb-[clamp(1.5rem,5vh,3rem)] pt-[clamp(6rem,13vh,8.5rem)]'
							: 'container-site'
					}
				>
					<h2
						id={TITLE_ID}
						data-demo-title
						className={`text-balance font-display text-[clamp(2.25rem,4.4vw,4.25rem)] font-bold leading-[1] tracking-[-0.035em] text-graphite dark:text-mineral ${
							pinned ? 'col-span-5 row-start-1 self-start' : 'max-w-3xl'
						}`}
					>
						{title}
					</h2>

					{pinned && <div aria-hidden data-stage-frame className={`${STAGE} ${FRAME}`} />}

					{beats.map((beat, index) => {
						const onStage = !pinned || index === active;
						return (
							<div
								key={beat.id}
								className={pinned ? 'contents' : `${index === 0 ? 'mt-14 md:mt-20' : 'mt-[clamp(5rem,12vw,9rem)]'} ${FLOW_BEAT}`}
								onFocus={() => onBeatFocus(index)}
							>
								<div
									data-beat-text
									className={
										pinned
											? `col-span-5 col-start-1 row-start-2 self-start pt-[clamp(1.5rem,5vh,3.25rem)] ${onStage ? '' : 'pointer-events-none'}`
											: 'lg:col-span-5 lg:pr-4'
									}
								>
									<div data-beat-inner>
										<p className="text-[15px] font-medium leading-none text-forest dark:text-sage">{beat.scale}</p>
										<h3 className="mt-4 text-balance font-display text-[clamp(1.75rem,2.5vw,2.5rem)] font-bold leading-[1.04] tracking-[-0.03em] text-graphite dark:text-mineral">
											{glue(beat.title)}
										</h3>
										<p className="mt-5 max-w-[34rem] text-pretty text-[1.0625rem] leading-[1.6] text-graphite-muted dark:text-dusk-muted">
											{glue(beat.text)}
										</p>
									</div>
								</div>

								<div
									data-beat-layer
									className={
										pinned
											? `relative overflow-hidden ${STAGE} ${LAYER_ORDER[index] ?? ''} ${onStage ? '' : 'pointer-events-none'}`
											: `relative mt-10 overflow-hidden ${FRAME} lg:col-span-7 lg:mt-0`
									}
								>
									{visual(index)}
								</div>
							</div>
						);
					})}

					{pinned && (
						<ScaleSteps
							items={beats.map((beat) => beat.scale)}
							active={active}
							onSelect={scrollToBeat}
							labelledBy={TITLE_ID}
							className="col-span-5 col-start-1 row-start-3 self-end"
						/>
					)}
				</div>
			</div>

			<div
				className={`container-site pb-[clamp(5rem,10vw,9rem)] ${
					pinned ? 'pt-[clamp(4.5rem,11vh,8rem)]' : 'pt-[clamp(5rem,12vw,9rem)]'
				}`}
			>
				<ClosingLine
					text={closing}
					className="max-w-[21ch] text-balance font-display text-[clamp(2.125rem,4.8vw,4.75rem)] font-bold leading-[1.02] tracking-[-0.035em] text-graphite dark:text-mineral"
				/>
			</div>
		</section>
	);
}
